/**
 * Building configuration definitions.
 * Separates developer-only settings for building stats, costs, construction times, and income.
 */

import { ConfigCategory, ConfigVisibility, ConfigSchema } from '../types';

/**
 * Building configuration values.
 * This is the runtime configuration that can be modified by developers.
 */
export interface BuildingConfig {
  // Building costs (developer-only)
  towerArcherCost: number;
  towerCannonCost: number;
  towerMagicCost: number;
  barracksCost: number;
  heroSpawnerCost: number;
  techBuildingCost: number;
  mineCost: number;

  // Construction times (developer-only)
  towerConstructionTime: number;
  mineConstructionTime: number;
  barracksConstructionTime: number;
  heroSpawnerConstructionTime: number;
  techBuildingConstructionTime: number;

  // Building health (developer-only)
  towerHealth: number;
  mineHealth: number;
  barracksHealth: number;
  heroSpawnerHealth: number;
  techBuildingHealth: number;
  wallHealth: number;

  // Building armor (developer-only)
  towerArmor: number;
  mineArmor: number;
  barracksArmor: number;
  heroSpawnerArmor: number;
  techBuildingArmor: number;
  wallArmor: number;

  // Repair settings (developer-only)
  repairProximityRange: number;
  repairRate: number; // HP per second
  repairCostPerTick: number;
  repairTickInterval: number;

  // Mining income (developer-only)
  miningBaseIncome: number; // Gold per 3 seconds at level 1
  miningIncomePerLevel: number; // Additional gold per level
  miningIncomeInterval: number; // Generate income every N seconds

  // Upgrade settings (developer-only)
  upgradeTime: number; // Time for all upgrades in seconds
  upgradeDamageMultiplier: number; // +X% per level (e.g., 0.1 for +10%)
  upgradeHealthMultiplier: number; // +X% per level (e.g., 0.2 for +20%)
  upgradeArmorAdditive: number; // +X per level (e.g., 1 for +1 per level)

  // Starting stats (developer-only)
  startingCurrency: number;
  // Future: starting units will also live here
  startingBuilderCount: number;

  // Visual / design (developer-only) - progress & health bars
  progressBarBackgroundColor: number;
  progressBarFillColor: number;
  progressBarBorderColor: number;
  progressBarPausedOverlayColor: number;
  progressBarBackgroundAlpha: number;
  progressBarBorderAlpha: number;
  progressBarPausedOverlayAlpha: number;
  // Note: bar thickness is fixed in code to keep UI simple for now

  healthBarBackgroundColor: number;
  healthBarHighColor: number;
  healthBarMidColor: number;
  healthBarLowColor: number;
  healthBarBorderColor: number;
  healthBarBackgroundAlpha: number;
  healthBarBorderAlpha: number;
  // Note: bar thickness is fixed in code to keep UI simple for now
}

/**
 * Default building configuration values.
 */
export const DEFAULT_BUILDING_CONFIG: BuildingConfig = {
  // Costs
  towerArcherCost: 50,
  towerCannonCost: 100,
  towerMagicCost: 75,
  barracksCost: 500,
  heroSpawnerCost: 750,
  techBuildingCost: 300,
  mineCost: 50,

  // Construction times (in seconds)
  towerConstructionTime: 20,
  mineConstructionTime: 20,
  barracksConstructionTime: 60,
  heroSpawnerConstructionTime: 120,
  techBuildingConstructionTime: 45,

  // Health
  towerHealth: 200,
  mineHealth: 200,
  barracksHealth: 500,
  heroSpawnerHealth: 500,
  techBuildingHealth: 300,
  wallHealth: 200,

  // Armor
  towerArmor: 5,
  mineArmor: 1,
  barracksArmor: 5,
  heroSpawnerArmor: 5,
  techBuildingArmor: 3,
  wallArmor: 10,

  // Repair
  repairProximityRange: 5,
  repairRate: 25, // HP per second
  repairCostPerTick: 5,
  repairTickInterval: 0.2,

  // Mining income
  miningBaseIncome: 5, // Gold per 3 seconds at level 1
  miningIncomePerLevel: 2, // +2 gold per level
  miningIncomeInterval: 3.0, // Every 3 seconds

  // Upgrades
  upgradeTime: 60, // 1 minute
  upgradeDamageMultiplier: 0.1, // +10% per level
  upgradeHealthMultiplier: 0.2, // +20% per level
  upgradeArmorAdditive: 1, // +1 per level

  // Starting stats
  startingCurrency: 1000,
  startingBuilderCount: 1,

  // Visual / design defaults (match BuildingRenderer defaults)
  progressBarBackgroundColor: 0x333333,
  progressBarFillColor: 0x0088ff,
  progressBarBorderColor: 0xffffff,
  progressBarPausedOverlayColor: 0xffff00,
  progressBarBackgroundAlpha: 0.8,
  progressBarBorderAlpha: 0.8,
  progressBarPausedOverlayAlpha: 0.5,

  healthBarBackgroundColor: 0x330000,
  healthBarHighColor: 0x00ff00,
  healthBarMidColor: 0xffff00,
  healthBarLowColor: 0xff0000,
  healthBarBorderColor: 0xffffff,
  healthBarBackgroundAlpha: 0.8,
  healthBarBorderAlpha: 0.8,
};

/**
 * Building configuration schema with metadata.
 * Defines which settings are visible/editable by developers.
 */
export const BUILDING_CONFIG_SCHEMA: ConfigSchema<BuildingConfig> = {
  // Building Costs
  towerArcherCost: {
    label: 'Archer Tower Cost',
    description: 'Cost to build Archer Tower (gold)',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1,
    max: 1000,
    step: 1,
  },
  towerCannonCost: {
    label: 'Cannon Tower Cost',
    description: 'Cost to build Cannon Tower (gold)',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1,
    max: 1000,
    step: 1,
  },
  towerMagicCost: {
    label: 'Magic Tower Cost',
    description: 'Cost to build Magic Tower (gold)',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1,
    max: 1000,
    step: 1,
  },
  barracksCost: {
    label: 'Barracks Cost',
    description: 'Cost to build Barracks (gold)',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1,
    max: 5000,
    step: 10,
  },
  heroSpawnerCost: {
    label: 'Hero Spawner Cost',
    description: 'Cost to build Hero Spawner (gold)',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1,
    max: 5000,
    step: 10,
  },
  techBuildingCost: {
    label: 'Tech Building Cost',
    description: 'Cost to build Tech Building (gold)',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1,
    max: 5000,
    step: 10,
  },
  mineCost: {
    label: 'Mining Node Cost',
    description: 'Cost to build Mining Node (gold)',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1,
    max: 1000,
    step: 1,
  },

  // Construction Times
  towerConstructionTime: {
    label: 'Tower Construction Time',
    description: 'Time to construct towers (seconds)',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1,
    max: 300,
    step: 1,
  },
  mineConstructionTime: {
    label: 'Mining Node Construction Time',
    description: 'Time to construct mining nodes (seconds)',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1,
    max: 300,
    step: 1,
  },
  barracksConstructionTime: {
    label: 'Barracks Construction Time',
    description: 'Time to construct Barracks (seconds)',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1,
    max: 600,
    step: 1,
  },
  heroSpawnerConstructionTime: {
    label: 'Hero Spawner Construction Time',
    description: 'Time to construct Hero Spawner (seconds)',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1,
    max: 600,
    step: 1,
  },
  techBuildingConstructionTime: {
    label: 'Tech Building Construction Time',
    description: 'Time to construct Tech Building (seconds)',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1,
    max: 600,
    step: 1,
  },

  // Building Health
  towerHealth: {
    label: 'Tower Health',
    description: 'Base health for all tower types (HP)',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1,
    max: 10000,
    step: 10,
  },
  mineHealth: {
    label: 'Mining Node Health',
    description: 'Base health for mining nodes (HP)',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1,
    max: 10000,
    step: 10,
  },
  barracksHealth: {
    label: 'Barracks Health',
    description: 'Base health for Barracks (HP)',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1,
    max: 10000,
    step: 10,
  },
  heroSpawnerHealth: {
    label: 'Hero Spawner Health',
    description: 'Base health for Hero Spawner (HP)',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1,
    max: 10000,
    step: 10,
  },
  techBuildingHealth: {
    label: 'Tech Building Health',
    description: 'Base health for Tech Building (HP)',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1,
    max: 10000,
    step: 10,
  },
  wallHealth: {
    label: 'Wall Health',
    description: 'Base health for Walls (HP)',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1,
    max: 10000,
    step: 10,
  },

  // Building Armor
  towerArmor: {
    label: 'Tower Armor',
    description: 'Base armor for all tower types',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 100,
    step: 1,
  },
  mineArmor: {
    label: 'Mining Node Armor',
    description: 'Base armor for mining nodes',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 100,
    step: 1,
  },
  barracksArmor: {
    label: 'Barracks Armor',
    description: 'Base armor for Barracks',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 100,
    step: 1,
  },
  heroSpawnerArmor: {
    label: 'Hero Spawner Armor',
    description: 'Base armor for Hero Spawner',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 100,
    step: 1,
  },
  techBuildingArmor: {
    label: 'Tech Building Armor',
    description: 'Base armor for Tech Building',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 100,
    step: 1,
  },
  wallArmor: {
    label: 'Wall Armor',
    description: 'Base armor for Walls',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 100,
    step: 1,
  },

  // Repair Settings
  repairProximityRange: {
    label: 'Repair Proximity Range',
    description: 'Builder must be within this many tiles to repair (tiles)',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1,
    max: 20,
    step: 1,
  },
  repairRate: {
    label: 'Repair Rate',
    description: 'Health repaired per second (HP/sec)',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1,
    max: 1000,
    step: 1,
  },
  repairCostPerTick: {
    label: 'Repair Cost Per Tick',
    description: 'Gold cost per repair tick',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 100,
    step: 1,
  },
  repairTickInterval: {
    label: 'Repair Tick Interval',
    description: 'Time between repair cost charges (seconds)',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0.1,
    max: 10,
    step: 0.1,
  },

  // Mining Income
  miningBaseIncome: {
    label: 'Mining Base Income',
    description: 'Gold per interval at level 1 (gold)',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1,
    max: 1000,
    step: 1,
  },
  miningIncomePerLevel: {
    label: 'Mining Income Per Level',
    description: 'Additional gold per level (gold)',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 100,
    step: 1,
  },
  miningIncomeInterval: {
    label: 'Mining Income Interval',
    description: 'Time between income generation (seconds)',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0.1,
    max: 60,
    step: 0.1,
  },

  // Upgrade Settings
  upgradeTime: {
    label: 'Upgrade Time',
    description: 'Time required for all upgrades (seconds)',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 1,
    max: 600,
    step: 1,
  },
  upgradeDamageMultiplier: {
    label: 'Upgrade Damage Multiplier',
    description: 'Damage increase per level (e.g., 0.1 = +10% per level)',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 1,
    step: 0.01,
  },
  upgradeHealthMultiplier: {
    label: 'Upgrade Health Multiplier',
    description: 'Health increase per level (e.g., 0.2 = +20% per level)',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 1,
    step: 0.01,
  },
  upgradeArmorAdditive: {
    label: 'Upgrade Armor Additive',
    description: 'Armor increase per level (e.g., 1 = +1 per level)',
    category: ConfigCategory.BUILDINGS,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 10,
    step: 1,
  },

  // Starting Stats
  startingCurrency: {
    label: 'Starting Currency',
    description: 'Initial gold the player starts with',
    category: ConfigCategory.GAMEPLAY,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 100000,
    step: 50,
  },
  startingBuilderCount: {
    label: 'Starting Builders',
    description: 'Number of Builder units to start with (for future use)',
    category: ConfigCategory.GAMEPLAY,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 20,
    step: 1,
  },

  // Visual / design - Progress Bar
  progressBarBackgroundColor: {
    label: 'Progress Bar Background Color',
    description: 'Background color for construction progress bar (hex number)',
    category: ConfigCategory.DESIGN,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 0xffffff,
    step: 1,
  },
  progressBarFillColor: {
    label: 'Progress Bar Fill Color',
    description: 'Fill color for construction progress bar (hex number)',
    category: ConfigCategory.DESIGN,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 0xffffff,
    step: 1,
  },
  progressBarBorderColor: {
    label: 'Progress Bar Border Color',
    description: 'Border color for construction progress bar (hex number)',
    category: ConfigCategory.DESIGN,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 0xffffff,
    step: 1,
  },
  progressBarPausedOverlayColor: {
    label: 'Progress Bar Paused Overlay Color',
    description: 'Overlay color when construction is paused (hex number)',
    category: ConfigCategory.DESIGN,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 0xffffff,
    step: 1,
  },
  progressBarBackgroundAlpha: {
    label: 'Progress Bar Background Alpha',
    description: 'Background alpha for construction progress bar (0-1)',
    category: ConfigCategory.DESIGN,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 1,
    step: 0.05,
  },
  progressBarBorderAlpha: {
    label: 'Progress Bar Border Alpha',
    description: 'Border alpha for construction progress bar (0-1)',
    category: ConfigCategory.DESIGN,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 1,
    step: 0.05,
  },
  progressBarPausedOverlayAlpha: {
    label: 'Progress Bar Paused Overlay Alpha',
    description: 'Overlay alpha when construction is paused (0-1)',
    category: ConfigCategory.DESIGN,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 1,
    step: 0.05,
  },

  // Visual / design - Health Bar
  healthBarBackgroundColor: {
    label: 'Health Bar Background Color',
    description: 'Background color for building health bar (hex number)',
    category: ConfigCategory.DESIGN,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 0xffffff,
    step: 1,
  },
  healthBarHighColor: {
    label: 'Health Bar High Color',
    description: 'Health bar color when health is high (hex number)',
    category: ConfigCategory.DESIGN,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 0xffffff,
    step: 1,
  },
  healthBarMidColor: {
    label: 'Health Bar Mid Color',
    description: 'Health bar color when health is medium (hex number)',
    category: ConfigCategory.DESIGN,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 0xffffff,
    step: 1,
  },
  healthBarLowColor: {
    label: 'Health Bar Low Color',
    description: 'Health bar color when health is low (hex number)',
    category: ConfigCategory.DESIGN,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 0xffffff,
    step: 1,
  },
  healthBarBorderColor: {
    label: 'Health Bar Border Color',
    description: 'Border color for building health bar (hex number)',
    category: ConfigCategory.DESIGN,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 0xffffff,
    step: 1,
  },
  healthBarBackgroundAlpha: {
    label: 'Health Bar Background Alpha',
    description: 'Background alpha for health bar (0-1)',
    category: ConfigCategory.DESIGN,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 1,
    step: 0.05,
  },
  healthBarBorderAlpha: {
    label: 'Health Bar Border Alpha',
    description: 'Border alpha for health bar (0-1)',
    category: ConfigCategory.DESIGN,
    visibility: ConfigVisibility.DEVELOPER,
    min: 0,
    max: 1,
    step: 0.05,
  },
};
